import PersonIcon from '@material-design-icons/svg/filled/person.svg?react';
import { type ChangeEvent, useRef, useState } from 'react';
import { toast } from 'sonner';
import { DialogAction } from '../../components/common/detailChrome';
import { Input } from '../../components/Input';
import { S3Image } from '../../components/S3Image';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { UrlValidationStatus } from '../../components/UrlValidationStatus';
import { useExternalUrlValidation } from '../../hooks/useExternalUrlValidation';
import { useTRPC } from '../../hooks/useTRPC';
import { captureAndReportError } from '../../utils/errorReporting';
import { publisherDisplayName, publisherWebsite } from './publisherSignals';
import type { SuperPublisherDetailData } from './types';

const MAX_AVATAR_SIZE_BYTES = 3_000_000;
const ALLOWED_AVATAR_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
]);
const FILE_EXTENSION_BY_MIME_TYPE: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
};

const LABEL_CLASS =
  'mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.08em] text-neutral-500';

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read the selected image'));
        return;
      }
      const [, base64 = ''] = result.split(',');
      resolve(base64);
    };
    reader.onerror = () => {
      reader.abort();
      reject(reader.error ?? new Error('Failed to read the selected image'));
    };
    reader.readAsDataURL(file);
  });
}

function getFileExtension(file: File): string | null {
  const fileNameExtension = file.name.split('.').pop()?.toLowerCase();
  if (fileNameExtension && ALLOWED_AVATAR_EXTENSIONS.has(fileNameExtension)) {
    return fileNameExtension;
  }
  return FILE_EXTENSION_BY_MIME_TYPE[file.type] ?? null;
}

function normalizeHttpsUrl(value: string): string {
  const trimmedValue = value.trim();
  if (!trimmedValue) return '';
  if (trimmedValue.startsWith('https://')) return trimmedValue;
  if (trimmedValue.startsWith('http://')) {
    return `https://${trimmedValue.slice('http://'.length)}`;
  }
  return `https://${trimmedValue}`;
}

function parsePublisherProfileInput(
  name: string,
  website: string,
  address: string,
  publisherType: 'Organization' | 'Individual',
) {
  const normalizedName = name.trim();
  if (!normalizedName) {
    throw new Error('Publisher name is required.');
  }

  const normalizedWebsite = normalizeHttpsUrl(website);
  if (!normalizedWebsite) {
    throw new Error('Publisher website is required.');
  }

  let parsedWebsite: URL;
  try {
    parsedWebsite = new URL(normalizedWebsite);
  } catch {
    throw new Error('Enter a valid publisher website.');
  }
  if (parsedWebsite.protocol !== 'https:') {
    throw new Error('Publisher website must use HTTPS.');
  }

  const normalizedAddress = address.trim();

  return {
    name: normalizedName,
    website: normalizedWebsite,
    address:
      publisherType === 'Organization' && normalizedAddress !== ''
        ? normalizedAddress
        : null,
  };
}

interface PublisherProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  publisher: SuperPublisherDetailData['publisher'];
  onSaved: () => Promise<void>;
}

/** Admin editor for the publisher's name, website, address and avatar. */
export function PublisherProfileDialog({
  open,
  onOpenChange,
  publisher,
  onSaved,
}: PublisherProfileDialogProps) {
  const trpc = useTRPC();
  const { organization, individual } = publisher;
  const currentName = organization?.name ?? individual?.name ?? '';
  const currentWebsite = publisherWebsite(publisher) ?? '';
  const currentAddress = organization?.address ?? '';

  const nameInputRef = useRef<HTMLInputElement>(null);
  const websiteInputRef = useRef<HTMLInputElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [websiteDraft, setWebsiteDraft] = useState(currentWebsite);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  const normalizedCurrentWebsite = currentWebsite.trim()
    ? normalizeHttpsUrl(currentWebsite)
    : '';
  const normalizedWebsiteDraft = websiteDraft.trim()
    ? normalizeHttpsUrl(websiteDraft)
    : '';
  const websiteReachability = useExternalUrlValidation(normalizedWebsiteDraft, {
    enabled: open && normalizedWebsiteDraft !== normalizedCurrentWebsite,
  });

  const handleAvatarSelection = async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const resetInput = () => {
      event.target.value = '';
    };

    const fileExtension = getFileExtension(file);
    if (!fileExtension) {
      toast.error('Upload a JPG, PNG, GIF, or WEBP image.');
      resetInput();
      return;
    }
    if (file.size > MAX_AVATAR_SIZE_BYTES) {
      toast.error('Avatar images must be 3 MB or smaller.');
      resetInput();
      return;
    }

    try {
      setIsUploadingAvatar(true);
      const imageBase64 = await fileToBase64(file);
      const result = await trpc.updateSuperPublisherAvatar.mutate({
        publisherUserJoinId: publisher.id,
        imageBase64,
        fileExtension,
      });

      if (result._tag === 'Left') {
        throw new Error(result.left.message);
      }

      toast.success(result.right.message);
      await onSaved();
    } catch (error) {
      captureAndReportError(error, {
        operation: 'superPublisherDetail.updateAvatar',
        component: 'PublisherProfileDialog',
        publisherUserJoinId: publisher.id,
      });
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to update publisher avatar',
      );
    } finally {
      setIsUploadingAvatar(false);
      resetInput();
    }
  };

  const handleSave = async () => {
    let payload: ReturnType<typeof parsePublisherProfileInput>;
    try {
      payload = parsePublisherProfileInput(
        nameInputRef.current?.value ?? currentName,
        websiteInputRef.current?.value ?? currentWebsite,
        addressInputRef.current?.value ?? currentAddress,
        publisher.type,
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to validate publisher profile',
      );
      return;
    }

    try {
      setIsSaving(true);
      const result = await trpc.updateSuperPublisherProfile.mutate({
        publisherUserJoinId: publisher.id,
        name: payload.name,
        website: payload.website,
        address: payload.address ?? undefined,
      });
      if (result._tag === 'Left') {
        throw new Error(result.left.message);
      }
      toast.success(result.right.message);
      onOpenChange(false);
      await onSaved();
    } catch (error) {
      captureAndReportError(error, {
        operation: 'superPublisherDetail.updateProfile',
        component: 'PublisherProfileDialog',
        publisherUserJoinId: publisher.id,
      });
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to update publisher profile',
      );
    } finally {
      setIsSaving(false);
    }
  };

  const isBusy = isSaving || isUploadingAvatar;

  return (
    <Dialog open={open} onOpenChange={(next) => !isBusy && onOpenChange(next)}>
      <DialogContent className="max-w-md rounded-2xl border-white/10 bg-[#161717] sm:rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-base">Edit publisher</DialogTitle>
          <DialogDescription className="text-xs">
            {publisherDisplayName(publisher)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {individual && (
            <div className="flex items-center gap-3">
              <S3Image
                src={individual.avatarUrl}
                thumbnailSrc={individual.avatarThumbnailUrl}
                alt=""
                aria-hidden="true"
                className="size-12 shrink-0 rounded-xl object-cover ring-1 ring-white/10"
                fallback={
                  <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-white/[0.04] ring-1 ring-white/10">
                    <PersonIcon className="size-6 fill-current text-neutral-500" />
                  </div>
                }
              />
              <div className="min-w-0">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  aria-label="Avatar image"
                  className="hidden"
                  onChange={handleAvatarSelection}
                />
                <DialogAction
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isBusy}
                >
                  {isUploadingAvatar
                    ? 'Uploading…'
                    : individual.avatarUrl || individual.avatarThumbnailUrl
                      ? 'Change avatar'
                      : 'Upload avatar'}
                </DialogAction>
                <p className="mt-1 text-[10.5px] text-neutral-600">
                  JPG, PNG, GIF, or WEBP up to 3 MB. Saves immediately.
                </p>
              </div>
            </div>
          )}

          <label className="block">
            <span className={LABEL_CLASS}>
              {publisher.type === 'Organization'
                ? 'Organization name'
                : 'Publisher name'}
            </span>
            <Input
              ref={nameInputRef}
              name="publisherName"
              defaultValue={currentName}
              required
            />
          </label>

          <label className="block">
            <span className={LABEL_CLASS}>Website</span>
            <Input
              ref={websiteInputRef}
              name="publisherWebsite"
              type="url"
              defaultValue={currentWebsite}
              required
              onChange={(event) => setWebsiteDraft(event.target.value)}
            />
            {websiteReachability.status !== 'idle' && (
              <UrlValidationStatus
                className="mt-1 justify-end"
                message={websiteReachability.message}
                status={websiteReachability.status}
              />
            )}
          </label>

          {publisher.type === 'Organization' && (
            <label className="block">
              <span className={LABEL_CLASS}>Address</span>
              <Input
                ref={addressInputRef}
                name="publisherAddress"
                defaultValue={currentAddress}
              />
            </label>
          )}
        </div>

        <DialogFooter className="gap-2">
          <DialogAction onClick={() => onOpenChange(false)} disabled={isBusy}>
            Cancel
          </DialogAction>
          <DialogAction
            onClick={() => {
              void handleSave();
            }}
            disabled={isBusy || websiteReachability.blocking}
            tone="primary"
          >
            {isSaving ? 'Saving…' : 'Save changes'}
          </DialogAction>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
